import { usePhraseContext } from '../PhraseContext';
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Modal,
  ModalProps,
  Radio,
  Tooltip,
  Upload,
  message,
  notification,
} from 'antd';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import {
  DeleteOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { Formik } from 'formik';
import FormField from 'components/FormField';
import { importExcelPhrases, uploadPhraseExcel } from 'services/api/phrase';
import styled from '@emotion/styled';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { What } from 'views/Aide/Scenario/PolicyTable';

interface FV {
  url: string;
  platform: General.PlatformUpperCase[];
  type: 'GROUP' | 'PERSON';
  autoTranslate: boolean;
}

function FileImportModal({
  refreshPhraseList,
  ...otherProps
}: ModalProps & { refreshPhraseList?: () => void }) {
  const { t } = useTranslate();

  const { platformList } = usePhraseContext();

  const initialValues = useMemo<FV>(() => {
    return {
      url: '',
      platform: platformList,
      type: 'PERSON',
      autoTranslate: true,
    };
  }, [platformList]);

  const [uploading, setUploading] = useState(false);

  const [file, setFile] = useState<File>();

  const [uploadResult, setUploadResult] = useState<{
    url: string;
    successCount: number;
    failCount: number;
  }>();

  const uploadRef = useRef(0);

  const lang = useSystemLanguageValue();

  const templateUrl = {
    'zh-CN':
      'https://boost-cluster.oss-cn-zhangjiakou.aliyuncs.com/static/excel/zh_CN.xlsx?OSSAccessKeyId=LTAI5tEhBtvCneNPZrcqQn6z&Expires=31729222593&Signature=7vCgi0iu5KTcqOC36zebRUOIPxk%3D',
    'en-US':
      'https://boost-cluster.oss-cn-zhangjiakou.aliyuncs.com/static/excel/en.xlsx?OSSAccessKeyId=LTAI5tEhBtvCneNPZrcqQn6z&Expires=2089221924&Signature=VWx2yJjq2HQEjxvceBMledlEbSI%3D',
    id: 'https://boost-cluster.oss-cn-zhangjiakou.aliyuncs.com/static/excel/id.xlsx?OSSAccessKeyId=LTAI5tEhBtvCneNPZrcqQn6z&Expires=5319221955&Signature=c%2BaE3Rw7Ts%2FHi4AhyyEsgdt037k%3D',
    th: 'https://boost-cluster.oss-cn-zhangjiakou.aliyuncs.com/static/excel/th.xlsx?OSSAccessKeyId=LTAI5tEhBtvCneNPZrcqQn6z&Expires=37729221977&Signature=JLzGIxfZBs3JL%2F%2Bd9Z3gltxrgDU%3D',
    tl: 'https://boost-cluster.oss-cn-zhangjiakou.aliyuncs.com/static/excel/ph.xlsx?OSSAccessKeyId=LTAI5tEhBtvCneNPZrcqQn6z&Expires=5329221956&Signature=diq3WTw2WyzCzB6sC8bsxzL8SLw%3D',
    es: 'https://boost-cluster.oss-cn-zhangjiakou.aliyuncs.com/static/excel/en.xlsx?OSSAccessKeyId=LTAI5tEhBtvCneNPZrcqQn6z&Expires=2089221924&Signature=VWx2yJjq2HQEjxvceBMledlEbSI%3D',
    pt: 'https://boost-cluster.oss-cn-zhangjiakou.aliyuncs.com/static/excel/pt.xlsx?OSSAccessKeyId=LTAI5tEhBtvCneNPZrcqQn6z&Expires=37729221964&Signature=IIDOh%2F8CxBZpDhkQC6pKhWZIV7U%3D',
    vi: 'https://boost-cluster.oss-cn-zhangjiakou.aliyuncs.com/static/excel/vn.xlsx?OSSAccessKeyId=LTAI5tEhBtvCneNPZrcqQn6z&Expires=37729221986&Signature=BlldmFzkO1cigOW79r%2F0E%2BafdoY%3D',
  }[lang];

  const getPermission = useGetPermission();

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, helpers) => {
        return importExcelPhrases(values)
          .then(() => {
            message.success(t('phrase.保存成功'));
            helpers.resetForm();
            setFile(undefined);
            setUploadResult(undefined);
            (otherProps.onCancel as any)?.();
            refreshPhraseList?.();
          })
          .catch(() => {
            message.error(t('phrase.保存失败，请稍后再试'));
          });
      }}
    >
      {({ values, setValues, isSubmitting, handleSubmit }) => {
        return (
          <Modal
            width={625}
            title={t('从文件导入')}
            footer={
              <div>
                <Checkbox
                  checked={values.autoTranslate}
                  onChange={({ target: { checked } }) =>
                    setValues({
                      ...values,
                      autoTranslate: checked,
                    })
                  }
                >
                  {t('自动翻译为站点语言')}
                </Checkbox>
                <Tooltip
                  title={t('将对表格中所有快捷回复进行翻译，包括已经与站点语言一致的回复内容')}
                >
                  <What className="mr-4 !ml-0" />
                </Tooltip>
                <Button onClick={otherProps.onCancel}>{t('取消')}</Button>
                <Button type="primary" loading={isSubmitting} onClick={() => handleSubmit()}>
                  {t('bs_14')}
                </Button>
              </div>
            }
            {...otherProps}
          >
            <StyledForm layout="vertical">
              <Alert
                banner
                type="info"
                message={t('请首先下载模板文件，依照格式填写内容，完成导入后快捷回复即可使用。')}
              />
              <FormField<FV['platform']>
                name="platform"
                required
                label={
                  <div>
                    {t('market.选择平台')}
                    <span>{t('将根据情况适配目标平台站点')}</span>
                  </div>
                }
                validate={(platform) =>
                  platform.length > 0 ? undefined : t('department.请选择x', { x: t('平台') })
                }
              >
                {({ field: { value, onChange, onBlur } }) => {
                  return (
                    <Checkbox.Group
                      value={value}
                      onChange={(value) => {
                        onChange(value as any);
                        setTimeout(() => {
                          onBlur();
                        });
                      }}
                    >
                      {platformList.map((platform) => {
                        return (
                          <Checkbox key={platform} value={platform}>
                            {platformLabelMap[toLowerCase(platform)]}
                          </Checkbox>
                        );
                      })}
                    </Checkbox.Group>
                  );
                }}
              </FormField>
              <FormField<FV['type']> name="type" required label={t('选择个人/团队')}>
                {({ field: { value, onChange } }) => {
                  return (
                    <Radio.Group
                      value={value}
                      onChange={({ target: { value } }) => onChange(value)}
                    >
                      <Radio value="PERSON">{t('groupType.PERSONAL')}</Radio>
                      {getPermission('快捷回复-团队模版') && (
                        <Radio value="GROUP">{t('groupType.TEAM')}</Radio>
                      )}
                    </Radio.Group>
                  );
                }}
              </FormField>
              <FormField<FV['url']>
                name="url"
                required
                label={t('ViewShops.上传文件')}
                validate={(url) => {
                  if (!url) {
                    return t('phrase.请上传文件');
                  }
                  if (uploadResult?.successCount === 0) {
                    return ' ';
                  }
                }}
              >
                {({ field: { onChange } }) => {
                  const withDownload = (...nodes: ReactNode[]) => (
                    <div>
                      <div className="flex justify-end mt-[-32px]">
                        <Button
                          style={{ paddingRight: 0 }}
                          type="link"
                          target="__blank"
                          href={templateUrl}
                          icon={<DownloadOutlined />}
                        >
                          {t('aide.下载模版')}
                        </Button>
                      </div>
                      {...nodes}
                    </div>
                  );

                  if (!file) {
                    return withDownload(
                      <Upload.Dragger
                        name="file"
                        accept=".xlsx,.xls"
                        beforeUpload={(file) => {
                          setFile(file);
                          setUploading(true);
                          const uploadRefCurrent = uploadRef.current;
                          return uploadPhraseExcel(file, lang)
                            .then((res) => {
                              if (uploadRef.current !== uploadRefCurrent) {
                                return;
                              }
                              setUploadResult(res);
                              onChange(res.url);
                              if (res.successCount > 0 && res.failCount > 0) {
                                notification.warn({
                                  message: t('aide.请注意'),
                                  description: t('importPhraseFileErrorTips', {
                                    count: res.failCount,
                                  }),
                                });
                              }
                            })
                            .catch((err) => {
                              console.log(err);
                              if (uploadRef.current !== uploadRefCurrent) {
                                return;
                              }
                              setFile(undefined);
                              message.error(t('phrase.上传失败'));
                            })
                            .then(() => {
                              setUploading(false);
                              return Upload.LIST_IGNORE;
                            });
                        }}
                      >
                        <FileExcelOutlined />
                        <p>{t('ViewShops.点击或者将文件拖拽到此区域上传')}</p>
                        <div>{t('ViewShops.限1个文件，文件拓展名为.xlsx .xls')}</div>
                      </Upload.Dragger>,
                    );
                  }

                  return withDownload(
                    <UploadResult>
                      {uploading ? <LoadingOutlined /> : <FileExcelOutlined />}
                      <div>{file.name}</div>
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          setFile(undefined);
                          setUploading(false);
                          setUploadResult(undefined);
                          uploadRef.current += 1;
                        }}
                      />
                    </UploadResult>,
                    uploadResult?.successCount === 0 && [
                      <div style={{ marginTop: '4px', color: '#F05B5B' }}>
                        {t('无有效数据，请重新上传')}
                      </div>,
                      <Button
                        style={{ paddingLeft: 0 }}
                        type="link"
                        href={uploadResult.url}
                        target="__blank"
                      >
                        {t('aide.下载表格')}
                      </Button>,
                    ],
                    uploadResult &&
                      uploadResult?.successCount > 0 &&
                      uploadResult.failCount > 0 && [
                        <div style={{ marginTop: '4px', color: '#F2A100' }}>
                          {t('importPhraseFileErrorTips', { count: uploadResult.failCount })}
                        </div>,
                        <Button
                          style={{ paddingLeft: 0 }}
                          type="link"
                          href={uploadResult.url}
                          target="__blank"
                        >
                          {t('aide.下载表格')}
                        </Button>,
                      ],
                  );
                }}
              </FormField>
            </StyledForm>
          </Modal>
        );
      }}
    </Formik>
  );
}

const StyledForm = styled(Form)`
  > .ant-alert {
    margin: -24px -24px 24px;
  }
  .ant-upload-drag-container {
    .anticon-file-excel {
      margin-bottom: 8px;
      font-size: 28px;
      color: var(--ant-primary-color);
    }
    p {
      margin-bottom: 4px;
      color: var(--ant-primary-color);
    }
    div {
      font-size: 12px;
      color: #b5b9bf;
    }
  }
`;

const UploadResult = styled.div`
  display: flex;
  align-items: center;
  padding: 14px;
  border: dashed 1px #d3d7e0;
  > .anticon {
    flex: 0 0 auto;
    font-size: 28px;
    margin-right: 14px;
  }
  > .anticon-file-excel {
    color: var(--ant-primary-color);
  }
  > .ant-button {
    flex: 0 0 auto;
    margin-left: 14px;
  }
  > div {
    flex: 1 0 0;
    white-space: pre-wrap;
    overflow: hidden;
  }
`;

export default FileImportModal;
